import { Instant, LocalDate } from "@js-joda/core";
import { PlanPriceListResp, ValidationType, Vehicle } from "./ApiTypes";
import { PaymentReportFilter, SubscriptionReportFilter } from "./Api";
import { ServerTransferConnectedAccountDropdown } from "../shared/api/axios-client";

export interface OperatorApi {
    operatorId: number;
    name: string;
    address: UsAddressApi;
    emailAddress: string;
    phoneNumber: string;
    website: string;
    logoUrl: string;
    topTitle: string;
    topText: string;
    bottomLeftTitle: string;
    bottomLeftText: string;
    bottomRightTitle: string;
    bottomRightText: string;
}

export interface FacilityInfoApi {
    facilityId: number;
    operatorId: number;
    name: string;
    description: string;
    timeZone: string;
    address: UsAddressApi;
    coordinates: CoordinatesApi;
    listImageId: number | null;
    entryInstructions: string;
    phoneNumber: string;
    emailAddress: string;
    clearanceInches: number;
    hasIntegrationsEnabled: boolean;
    features: string[];
}

export interface UsAddressApi {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
    latitude?: number;
    longitude?: number;
    timezone?: string;
}

export interface CoordinatesApi {
    lat: number;
    lng: number;
}

export interface OrderDebtApi {
    orderId: number;
    createdWhen: string;
    orderTotal: number;
    paid: number;
    unpaid: number;
}

export interface VehicleApi {
    vehicleId: number;
    make: string;
    model: string;
    color: string;
    stateCode: string;
    licensePlateNumber: string;
}

export interface RateProgramApi {
    rateProgramId: number;
    facilityId: number;
    name: string;
    start: string;
    end: string | null;
    inAfter: string | null;
    inBefore: string | null;
    outAfter: string | null;
    outBefore: string | null;
    applicableDays: number | null;
    isEvent: boolean;
    entries: RateEntryApi[];
    isActive: boolean;
    isValidation: boolean;
    canBeValidated: boolean;
    validationType: ValidationType;
    encodedRateProgramId: string;
}

export interface RateEntryApi {
    rateEntryId: number;
    rateProgramId: number;
    upTo: number;
    rate: number;
}

export interface FacilityImageApi {
    facilityImageId: number;
    storedImageId: number;
    orderId: number;
    caption: string;
    altText: string;
}

export interface ListedPlanApi {
    planId: number;
    planEntryId: number;
    name: string;
    description: string;
    accessHours: string;
    dayOfMonthInvoice: number;
    dayOfMonthDue: number;
    dayOfMonthLate: number;
    price: number;
}

export interface OrderApi {
    orderId: number;
    initiator: OrderInitiator;
    initiatedByStaffId: number | null;
    parkerId: number;
    facilityId: number;
    price: number;
    createdWhen: string;
    processorPaymentIntentKey: string | null;
    type: OrderType;
    facility: FacilityInfoApi;
    submittedWhen: string | null;
    balance: OrderBalanceApi;
    summary: string;
}

export interface OrderDetailsApi {
    order: OrderApi;
    balanceChanges: OrderBalanceApi[];
    summary: string;
}

export interface OrderBalanceApi {
    orderBalanceId: number;
    type: OrderBalanceType;
    orderId: number;
    createdWhen: string;
    debt: number;
    paid: number;
    balance: number;
    totalDebt: number;
    totalPaid: number;
    reason: string | null;
    paymentId: number | null;
}

export interface InvoiceApi {
    invoiceId: number;
    invoiceNumber: string;
    createdWhen: string;
    dueWhen: string;
    openingBalance: number;
    amountDue: number;
    endingBalance: number;
    emailSentWhen: string | null;
    facility: FacilityInfoApi;
    orders: OrderInfoApi[];
    parker: ParkerInfoApi;
}

export interface PaymentApi {
    paymentId: number;
    parentPaymentId: number | null;
    facilityId: number;
    facility: FacilityLiteApi;
    type: PaymentType;
    amount: number;
    remaining: number;
    unapplied: number;
    paymentMethod: string;
    paymentIntentKey: string | null;
    chargeKey: string | null;
    paymentKey: string | null;
    changes: PaymentChangeApi[];
    applications: ApplyPaymentApi[];
    createdWhen: string;
    paidWhen: string;
    status: string;
    checkDate?: LocalDate;
    checkNumber?: string;
    checkMemo?: string;
    staffName?: string;
}

export interface ApplyPaymentApi {
    orderBalanceId: number;
    type: OrderBalanceType;
    facilityId: number;
    parkerId: number;
    orderId: number;
    debt: number;
    paid: number;
    balance: number;
    totalDebt: number;
    totalPaid: number;
    createdWhen: string;
}

export interface OrderInfoApi {
    orderId: number;
    initiator: OrderInitiator;
    initiatedByStaffId: number | null;
    parkerId: number;
    facilityId: number;
    price: number;
    createdWhen: string;
    processorPaymentIntentKey: string | null;
    orderType: OrderType;
    submittedWhen: string | null;
    balance: OrderBalanceApi;
    summary: string;
}

export interface PaymentChangeApi {
    paymentId: number;
    parentPaymentId: number | null;
    facilityId: number;
    parkerId: number;
    type: PaymentType;
    amount: number;
    balance: number;
    createdWhen: string;
}

export interface LogInReqApi {
    emailAddress: string;
    password: string;
}

export interface ChangeNameReqApi {
    name: string;
}

export interface ChangePhoneReqApi {
    phoneNumber: string;
}

export interface StripeLinkRespApi {
    isOnboarded: boolean;
    connectLink: string | null;
}

export interface ChangePasswordReqApi {
    currentPassword: string;
    newPassword: string;
}

export interface ForgotPasswordReqApi {
    emailAddress: string;
}

export interface StaffApi {
    staffId: number;
    role: StaffRole;
    operatorId: number;
    name: string;
    note: string;
    emailAddress: string;
    phoneNumber: string;
    createdWhen: string;
    removedWhen: string | null;
}

export interface StaffNotificationApi {
    staffNotificationId: number;
    staffId: number;
    notificationType: NotificationType,
    createdWhen: Instant;
}

export interface StaffNotificationUpdateReqApi {
    notificationType: NotificationType,
    enable: boolean;
}

export interface StaffUpdateReqApi {
    name: string;
    emailAddress: string;
    phoneNumber: string;
    note: string;
}

export interface InviteStaffReqApi {
    role: StaffRole;
    name: string;
    emailAddress: string;
    phoneNumber: string;
    password: string;
    note: string;
}

export interface SearchResultApi {
    searchResultId: number;
    type: SearchEntityType;
    id: number;
    name: string;
    facilityName: string;
    facilityId: number;
}

export interface PlanCreateReqApi {
    facilityId: number;
    name: string;
    accessHours: string;
    description: string;
    dayOfMonthInvoice: number;
    dayOfMonthDue: number;
    dayOfMonthLate: number;
    initialPrice: number;
    terms: string;
    isPrivate: boolean;
}

export interface PlanUpdateReqApi {
    planEntryId: number;
    name: string;
    description: string;
    accessHours: string;
    planPriceId: number | null;
    dayOfMonthInvoice: number;
    dayOfMonthDue: number;
    dayOfMonthLate: number;
    note: string;
    terms: string;
}

export interface PlanPriceAddReqApi {
    name: string;
    price: number;
    couponCode: string;
}

export interface PlanPriceListRespApi {
    planPriceId: number;
    name: string;
    price: number;
    couponCode: string;
}

export interface ReservationApi {
    reservationId: number;
    facilityId: number;
    parkerId: number;
    start: string;
    end: string;
    actualStart: string;
    actualEnd: string;
    duration: number;
    rateProgramId: number;
    price: number;
    createdWhen: string;
    rateProgram: RateProgramInfoApi;
    validationRequestId?: number;
    confirmationNum: string;
    spaces: string;
}

export interface SubscriptionApi {
    subscriptionId: number;
    parkerId: number;
    status: SubscriptionStatus;
    start: string;
    facility: FacilityInfoApi;
    accessPeriods: AccessPeriodInfoApi[];
    parkers: SubscriptionParkerApi[];
    vehicles: VehicleApi[];
    paymentMethod: string | null;
    prices: PlanPriceListResp[];
    planEntryId: number;
    planPriceId: number;
    quantity: number;
    end: string | null;
    isAccountsReceivable: boolean;
    price: number;
    plan: PlanInfoApi;
    isEnhancedGroup: boolean;
}

export interface PlanInfoApi {
    planId: number;
    facilityId: number;
    name: string;
    description: string;
    accessHours: string;
    dayOfMonthInvoice: number;
    dayOfMonthDue: number;
    dayOfMonthLate: number;
}

export interface FacilityApi {
    facilityId: number;
    name: string;
    description: string;
    timeZone: string;
    address: UsAddressApi;
    coordinates: CoordinatesApi;
    listImageId: number | null;
    entryInstructions: string;
    physicalCapacity: number;
    enforcedCapacity: number;
    clearanceInches: number;
    website: string;
    emailAddress: string;
    phoneNumber: string;
    images: FacilityImageApi[];
    hourlyRates: RateProgramApi[];
    listedPlans: ListedPlanApi[];
    features: string[];
    payoutAccount: ServerTransferConnectedAccountDropdown;
}

export interface FacilityLiteApi {
    facilityId: number;
    name: string;
    description: string;
    timeZone: string;
    address: UsAddressApi;
    coordinates: CoordinatesApi;
    listImageId: number | null;
    entryInstructions: string;
    physicalCapacity: number;
    enforcedCapacity: number;
    clearanceInches: number;
    website: string;
    emailAddress: string;
    phoneNumber: string;
}

export interface ParkerApi {
    parkerId: number;
    name: string;
    emailAddress: string;
    phoneNumber: string | null;
    note: string;
}

export interface ParkerListEntryApi {
    parkerId: number;
    name: string;
    emailAddress: string;
    phoneNumber: string | null;
    note: string;
    balance: number;
    monthly: number;
}

export interface ParkerDetailsApi {
    parkerId: number;
    name: string;
    note: string;
    emailAddress: string;
    phoneNumber: string | null;
    balance: number;
    subscriptions: ParkerSubInfoApi[];
    reservations: ReservationApi[];
    vehicles: VehicleApi[];
    subscriptionParkerId: number | null;
    staffRole: StaffRole
    nonRenewedSubscriptionIds: number[];
}

export interface ParkerDetailsGridResultApi {
    parkerDetailsGrid: ParkerDetailsGridApi[];
    timeZone: string;
}

export interface ParkerDetailsGridApi {
    parkerId: number;
    name: string;
    note: string;
    emailAddress: string;
    phoneNumber: string | null;
    balance: number;
    status: SubscriptionStatus;
    mrr: number;
    guestCount: number;
    spotCount: number;
    planCount: number;
    createdWhen: string;
    planName: string;
    unitPrice: number;
    subscriptionId: number;
    isExpired: boolean;
    isActive: boolean;
    inviteId: number;
    hasAutoPay: boolean;
    hasNonRenewedPeriod: boolean;
}

export interface CheckCreateReqApi {
    checkDate: string;
    checkNumber: string;
    checkMemo: string;
    amount: number;
}

export interface PmtApplyApi {
    orderId: number;
    amount: number;
}

export interface PlanApi {
    planId: number;
    planEntryId: number;
    name: string;
    description: string;
    accessHours: string;
    dayOfMonthInvoice: number;
    dayOfMonthDue: number;
    dayOfMonthLate: number;
    planPriceId: number | null;
    planPrice: PlanPriceInfoApi | null;
    prices: PlanPriceDetailsApi[];
    note: string;
    isPrivate: boolean;
    prorate: string | null;
    terms: string;
    approvalEmailText: string;
    isOneTermPlan: boolean;
}

export interface PlanDetailsApi {
    planId: number;
    planEntryId: number;
    name: string;
    description: string;
    accessHours: string;
    dayOfMonthInvoice: number;
    dayOfMonthDue: number;
    dayOfMonthLate: number;
    planPriceId: number | null;
    terms: string;
    approvalEmailText: string;
    isPrivate: boolean;
    planPrice: PlanPriceInfoApi | null;
    prices: PlanPriceDetailsApi[];
    history: PlanChangeApi[];
    prorate: string | null;
}

export interface PlanChangeApi {
    planEntryId: number;
    planId: number;
    name: string;
    description: string;
    accessHours: string;
    dayOfMonthInvoice: number;
    dayOfMonthDue: number;
    dayOfMonthLate: number;
    planPriceId: number | null;
    planPrice: PlanPriceInfoApi | null;
    createdWhen: string;
    createdByStaffId: number;
    note: string;
}

export interface PlanPriceInfoApi {
    planPriceId: number;
    planId: number;
    name: string;
    price: number;
    createdByStaffId: number;
    createdWhen: string;
}

export interface PlanPriceDetailsApi {
    planPriceId: number;
    planId: number;
    name: string;
    price: number;
    createdByStaffId: number;
    createdWhen: string;
    subscribers: SubscriberInfoApi[];
    couponCode: string;
}

export interface InviteParkerReqApi {
    name: string;
    emailAddress: string;
    phoneNumber: string | null;
    planId: number;
    planPriceId: number;
    quantity: number;
    message: string;
    requirePayment: boolean;
    startDate: LocalDate;
    IsEnhancedGroup: boolean;
}

export interface FacilityCreateReqApi {
    name: string;
    address: UsAddressApi;
    description: string;
    instructions: string;
    postedCapacity: number;
    enforcedCapacity: number;
    website: string;
    emailAddress: string;
    phoneNumber: string;
    clearanceInches: number;
    imageThumb: string;
    bannerImage: string;
    latitude: number;
    longitude: number;
    timezone: string;
}

export interface FacilityUpdateReqApi {
    facilityId: number;
    name: string;
    address: UsAddressApi;
    description: string;
    instructions: string;
    postedCapacity: number;
    enforcedCapacity: number;
    clearanceInches: number;
    website: string;
    emailAddress: string;
    phoneNumber: string;
    latitude: number;
    longitude: number;
    timezone: string;
}

export interface SubscriptionSavedReportAddReqApi {
    facilityId: number;
    name: string;
    filter: SubscriptionReportFilter;
}

export interface SubscriptionSavedReportsApi {
    savedReportId: number;
    name: string;
    filter: SubscriptionReportFilter;
}

export interface PaymentSavedReportAddReqApi {
    facilityId: number;
    name: string;
    filter: PaymentReportFilter;
}

export interface PaymentSavedReportsApi {
    savedReportId: number;
    name: string;
    filter: PaymentReportFilter;
}

export interface ParkerAgingRecordApi {
    name: string;
    emailAddress: string;
    balance30Days: number;
    balance60Days: number;
    balance90Days: number;
    totalBalance: number;
}

export interface ParkerInfoApi {
    parkerId: number;
    operatorId: number;
    name: string;
    emailAddress: string;
    phoneNumber: string | null;
    createdWhen: string;
    vehicles: Vehicle[];
    isManaged: boolean;
}

export interface RateProgramDataApi {
    facilityId: number;
    name: string;
    start: string;
    end: string | null;
    inAfter: string | null;
    inBefore: string | null;
    outAfter: string | null;
    outBefore: string | null;
    isEvent: boolean;
    isValidation: boolean;
    applicableDays: number | null;
    entries: RateEntryDataApi[];
    canBeValidated: boolean;
    validationType: ValidationType;
}

export interface RateProgramInfoApi {
    rateProgramId: number;
    facilityId: number;
    name: string;
    start: string;
    end: string | null;
    inAfter: string | null;
    inBefore: string | null;
    outAfter: string | null;
    outBefore: string | null;
    isEvent: boolean;
}

export interface RateEntryDataApi {
    upTo: number;
    rate: number;
}

export interface SubscriberInfoApi {
    parkerId: number;
    name: string;
    note: string;
    emailAddress: string;
    phoneNumber: string | null;
    quantity: number;
}

export interface SubscriptionUpdateReqApi {
    subscriptionEntryId: number;
    subscriptionId: number;
    planPriceId: number;
    quantity: number;
    status: SubscriptionStatus;
    start: string;
    end: string | null;
    isAccountsReceivable: boolean;
    isLateFeeEnabled: boolean;
    applyQuantityChangeNow: boolean;
}

export interface ParkerImportReqApi {
    file: File;
}

export interface SubEntryApi {
    subscriptionEntryId: number;
    subscriptionId: number;
    planId: number;
    planEntryId: number;
    planPriceId: number;
    quantity: number;
    status: SubscriptionStatus;
    start: string;
    end: string | null;
    isAccountsReceivable: boolean;
    isLateFeeEnabled: boolean;
    createdWhen: string;
}

export interface SubEntryForEditApi {
    prices: PlanPriceInfoApi[];
    subscriptionEntryId: number;
    subscriptionId: number;
    planId: number;
    planEntryId: number;
    planPriceId: number;
    quantity: number;
    status: SubscriptionStatus;
    start: string;
    end: string | null;
    isAccountsReceivable: boolean;
    isLateFeeEnabled: boolean;
    createdWhen: string;
    applyQuantityChangeNow: boolean;
}

export interface AccessPeriodInfoApi {
    accessPeriodId: number;
    subscriptionEntryId: number;
    quantity: number;
    start: string;
    end: string;
    isDelivered: boolean;
    price: number;
}

export interface SubscriptionParkerApi {
    parker: ParkerInfoApi;
    quantity: number;
    vehicles: VehicleApi[];
    subscriptionGroupId?: number;
    subscriptionGroupName: string;
    startDate?: string;
}

export interface ParkerUpdateReqApi {
    name: string;
    emailAddress: string;
    phoneNumber: string | null;
    note: string;
}

export interface SubListEntryApi {
    subscriptionId: number;
    parkerId: number;
    status: SubscriptionStatus;
    quantity: number;
    unitPrice: number | null;
    price: number | null;
    plan: PlanInfoBasicApi;
    facility: FacilityInfoApi;
}

export interface PlanInfoBasicApi {
    planId: number;
    planEntryId: number;
    name: string;
    accessHours: string;
}

export interface FacilitySubApi {
    subscriptionId: number;
    parkerId: number;
    status: SubscriptionStatus;
    quantity: number;
    unitPrice: number;
    price: number;
    createdWhen: string;
    parker: ParkerInfoApi;
    plan: PlanInfoBasicApi;
    facility: FacilityInfoApi;
}

export interface DeleteConfirmApi {
    deleteCode: string;
}

export interface ChangeEmailReqApi {
    currentPassword: string;
    newEmailAddress: string;
}

export interface VehicleDetailsApi {
    vehicleId: number;
    make: string;
    model: string;
    color: string;
    stateCode: string;
    licensePlateNumber: string;
    links: VehicleLinkApi[];
}

export interface VehicleLinkApi {
    type: VehicleLinkType;
    reservationId: number | null;
    subscriptionId: number | null;
    reservation: ReservationApi | null;
    subscription: SubListEntryApi | null;
}

export interface FacilityInvoiceApi {
    invoiceId: number;
    invoiceNumber: string;
    createdWhen: string;
    dueWhen: string;
    openingBalance: number;
    amountDue: number;
    endingBalance: number;
    emailSentWhen: string | null;
    orders: OrderInfoApi[];
    parker: ParkerInfoApi;
}

export interface ResetCodeCheckReqApi {
    resetPasswordCode: string;
}

export interface ResetPasswordReqApi {
    resetPasswordCode: string;
    newPassword: string;
}

export interface AccessKeyApi {
    accessKeyId: number;
    facilityId: number;
    accessKeyNumber: string;
    createdWhen: string;
    createdByStaff: StaffApi;
    note: string;
    facility: FacilityInfoApi;
    links: AccessKeyLinkInfoApi[];
}

export interface AccessKeyListEntryApi {
    accessKeyId: number;
    accessKeyNumber: string;
    createdWhen: string;
    note: string;
}

export interface AccessKeyLinkInfoApi {
    accessKeyLinkId: number;
    accessKeyId: number;
    type: AccessKeyLinkType;
    createdWhen: string;
    note: string;
    subscription: SubscriptionInfoApi | null;
    guest: SubscriptionParkerAccessKeyApi | null;
    vehicle: SubscriptionParkerVehicleAccessKeyApi | null;
}

export interface SubscriptionInfoApi {
    subscriptionId: number;
    status: SubscriptionStatus;
    start: string;
    parker: ParkerInfoApi;
}

export interface AccessKeyReqApi {
    accessKeyNumber: string;
    note: string;
}

export interface AccessKeyAssignSubApi {
    accessKeyId: number;
    subscriptionId: number;
    note: string | null;
}

export interface AccessKeyParkerAssignReqApi {
    accessKeyId: number;
    subscriptionParkerId: number;
    note: string | null;
}

export interface AccessKeyVehicleSubAssignReqApi {
    accessKeyId: number;
    vehicleSubscriptionId: number;
    note: string | null;
}

export interface SubscriptionParkerAccessKeyApi {
    subscription: SubscriptionInfoApi;
    parker: ParkerInfoApi;
}

export interface SubscriptionParkerVehicleAccessKeyApi {
    isPrimary: boolean;
    subscription: SubscriptionInfoApi;
    parker: ParkerInfoApi;
    vehicle: VehicleApi;
}

export interface FacilityStatsApi {
    totalMonthlyParkerCount: number;
    pendingParkers: number;
    activeReservations: number;
    upcomingReservations: number;
    monthlyRecurringRevenue: number;
    currentMonthTransientRevenue: number;
    totalUnpaidBalance: number;
}

export interface SubscriptionStatApi {
    status: SubscriptionStatus;
    start: string;
    end: string | null;
    qty: number;
    unit: number;
    total: number;
}

export interface ReservationStatApi {
    start: string;
    end: string;
    price: number;
}

export interface OrderStatApi {
    created: string;
    price: number;
    type: OrderType;
    isPaid: boolean;
}

export interface ParkerSubInfoApi {
    subscriptionId: number;
    parkerId: number;
    status: SubscriptionStatus;
    quantity: number;
    unitPrice: number;
    price: number;
    createdWhen: string;
    guests: SubscriptionParkerApi[];
    plan: PlanInfoBasicApi;
    hasAutoBillingEnabled: boolean;
    facilityHasIntegrationsEnabled: boolean;
}

export interface FacilityPaymentApi {
    paymentId: number;
    parentPaymentId: number | null;
    type: PaymentType;
    amount: number;
    balance: number;
    remaining: number;
    paymentMethod: string;
    paymentIntentKey: string | null;
    chargeKey: string | null;
    createdWhen: string;
    parker: ParkerInfoApi;
    changes: PaymentChangeApi[];
    applications: ApplyPaymentApi[];
}

export interface BalanceChangeApi {
    number: number;
    type: BalanceChangeType;
    paidWhen: string;
    payment: PaymentApi | null;
    orderBalance: OrderBalanceApi | null;
    invoice: InvoiceInfoApi | null;
    rank: number;
    balance: number;
}

export interface ImportPaymentReqApi {
    note: string;
    amount: number;
}

export interface SessionApi {
    parker: ParkerInfoApi;
    type: SessionType;
    subscription: SessionSubscriptionApi | null;
    subscriptionGuest: SessionSubscriptionApi | null;
    reservation: ReservationApi | null;
    vehicles: string;
}

export interface SessionSubscriptionApi {
    subscriptionId: number;
    parkerId: number;
    status: SubscriptionStatus;
    guests: SubscriptionParkerApi[];
    plan: PlanInfoBasicApi;
    accessPeriods: SessionAccessPeriodApi[];
}

export interface SessionAccessPeriodApi {
    subscriptionId: number;
    parkerId: number;
    quantity: number;
    price: number;
    createdWhen: string;
    guests: SubscriptionParkerApi[];
    plan: PlanInfoBasicApi;
    orderBalance: OrderBalanceApi;
}

export interface PlanInvoiceReqApi {
    year: number;
    month: number;
}

export interface PlanInvoiceRespApi {
    invoice: InvoiceInfoApi;
    emailArgs: InvoiceArgsApi;
}

export interface InvoiceInfoApi {
    invoiceId: number;
    invoiceNumber: string;
    orderId: number;
    createdWhen: string;
    dueWhen: string;
    openingBalance: number;
    amountDue: number;
    endingBalance: number;
    emailSentWhen: string | null;
    parker: ParkerInfoApi;
}

export interface InvoiceArgsApi {
    facilityName: string;
    parkerName: string;
    invoiceId: string;
    invoiceDate: string;
    accessPeriodStart: string;
    accessPeriodEnd: string;
    openingBalance: string;
    orderDesc: string;
    orderPrice: string;
    totalDue: string;
    staffName: string;
    staffEmail: string;
    link: string;
    linkText: string;
    operator: OperatorApi | null;
}

export interface SubscriptionParkerInviteReqApi {
    subscriptionId: number;
    quantity: number;
    name: string;
    emailAddress: string;
    isLoginEnabled: boolean
    subscriptionGroupId: number | null;
    startDate?: string;
}

export interface SubscriptionParkerChangeReqApi {
    parkerId: number;
    quantity: number;
}

export interface SubscriptionParkerRemoveReqApi {
    parkerId: number;
}

export interface VehicleAddReqApi {
    vehicleId: number;
}

export interface VehicleDeleteReqApi {
    vehicleId: number;
}

export interface VehicleLinkRespApi {
    vehicleLinkId: number;
    vehicleId: number;
    type: VehicleLinkType;
    subscriptionId: number;
}

export interface OrderCustomReqApi {
    name: string;
    emailAddress: string;
    phoneNumber: string | null;
    description: string;
    items: OrderCustomItemReqApi[];
    dueDate: string;
}

export interface OrderCustomItemReqApi {
    category: string | null;
    name: string;
    unitPrice: number;
    quantity: number;
}

export interface FacilityPayoutApi {
    payoutId: number;
    facilityId: number;
    payoutKey: string;
    amount: number;
    createdWhen: string;
    processorCreatedWhen: string;
    arrivalWhen: string;
    transactions: PayoutTransactionApi[];
}

export interface PayoutTransactionApi {
    payoutTransactionId: number;
    payoutId: number;
    balanceTransactionKey: string;
    type: string;
    key: string;
    paymentIntentKey: string | null;
    chargeKey: string | null;
    refundKey: string | null;
    disputeKey: string | null;
    payment: PayoutTransactionPaymentApi | null;
    gross: number;
    fee: number;
    net: number;
    createdWhen: string;
    processorCreatedWhen: string;
    availableWhen: string;
}

export interface PayoutTransactionPaymentApi {
    paymentId: number;
    parentPaymentId: number | null;
    type: PaymentType;
    amount: number;
    balance: number;
    createdWhen: string;
    parker: ParkerInfoApi;
    applications: ApplyPaymentApi[];
}

export interface OrderAdjustReqApi {
    orderId: number;
    currentTotalDebt: number;
    newTotalDebt: number;
    reason: string;
}

export interface PaymentAdjustReqApi {
    paymentId: number;
    currentAmount: number;
    newAmount: number;
    note: string;
}

export interface PlanBillInitRespApi {
    parker: ParkerInfoApi;
    order: OrderInfoApi;
}

export interface PlanBillConfirmRespApi {
    parker: ParkerInfoApi;
    order: OrderInfoApi;
    success: boolean;
    amountPaid: number;
    error: string;
}

export enum NotificationType {
    MonthlyApplication = 0,
    MonthlyApplicationDaily = 1,
    RenewalRetriesExhausted = 2,
    DisputeAlert = 3,
    ParkerSubscriptionCancellation = 4,
    ParkerSubscriptionModified = 5,
    ValidationRequestCreated = 6,
    TransientParkerPaid = 7,
    ValidationRequestPurchased = 8,
    SubscriptionRenewalSummary = 9,
    ACHPaymentFailure = 10
}

export enum OrderType {
    Imported = 5,
    Reserve = 10,
    Subscribe = 20,
    ModifySubscription = 30,
    AccessPeriod = 40,
    Custom = 50,
    EnhancedModifySubscription = 60,
}

export enum PaymentType {
    ImportedPayment = 20,
    CashPayment = 40,
    AchPayment = 60,
    AchFailed = 80,
    CheckPayment = 100,
    CheckBounce = 120,
    WriteCheck = 140,
    ProcessorPayment = 160,
    ProcessorRefund = 180,
    ProcessorDisputeChange = 200,
    Adjustment = 220,
}

export enum OrderInitiator {
    System = 0,
    Parker = 1,
    Staff = 2,
}

export enum OrderBalanceType {
    OrderPlaced = 10,
    OrderFee = 20,
    ApplyPayment = 30,
    UndoApplyPayment = 40,
    RefundPayment = 50,
    OrderAdjustment = 60,
}

export enum SubscriptionStatus {
    Applied = 0,
    Rejected = 1,
    WaitingForContract = 2,
    Active = 3,
    Cancelled = 4,
    Invited = 5,
    Waitlisted = 6,
    NeedsSetup = 7,
    FinalizeSetup = 8
}

export enum IntegrationStatus {
    Deactivated,
    Activated
}

export enum VehicleLinkType {
    Reservation = 0,
    Subscription = 1,
}

export enum StaffRole {
    SiteAdmin = 0,
    OperatorAdmin = 1,
}

export enum UserRole
{
    Owner
}

export enum UserPaymentType
{
    Invoiced,
    Recurring
}

export enum SearchEntityType {
    Staff = 10,
    Parker = 20,
    Order = 30,
    Payment = 40,
    Invoice = 50,
    Reservation = 60,
    Plan = 70,
    Vehicle = 80,
    AccessKey = 90,
}

export enum AccessKeyLinkType {
    Subscription = 10,
    SubscriptionParker = 20,
    VehicleSubscription = 30,
}

export enum BalanceChangeType {
    Payment = 0,
    OrderBalance = 1,
    Invoice = 2,
}

export enum SessionType {
    Subscription = 0,
    SubscriptionParker = 1,
    Reservation = 2,
}


